import {
  substituteMarketingBrandingInText,
  type MarketingBrandingSubstituteOptions,
} from './marketingSiteContact.js';

const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

function formatNumberWithCommas(amount: number): string {
  const rounded = Math.round(amount);
  const amountStr = String(rounded);
  if (amountStr.length <= 3) return amountStr;
  const lastThree = amountStr.slice(-3);
  const remaining = amountStr.slice(0, -3);
  const withCommas = remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return `${withCommas},${lastThree}`;
}

export function formatTenantCurrency(amount: number, currencyCode = 'INR'): string {
  const symbol = CURRENCY_SYMBOLS[currencyCode] ?? currencyCode;
  return `${symbol}${formatNumberWithCommas(amount)}`;
}

const LLMS_TIER_AMOUNTS = [4799, 7499, 9499, 12499, 8499, 10999, 13999, 18499, 24499, 11000, 33000, 44000];

const LLMS_COMMON_AMOUNTS = [
  6, 8, 12, 15, 200, 300, 400, 500, 600, 700, 800, 850, 900, 999, 1000, 1200, 1300, 1500, 1800, 1999, 2000,
  2499, 2500, 2800, 2999, 3000, 3200, 3500, 3999, 4000, 4500, 4999, 5000, 5999, 6000, 6999, 7000, 7500, 8000,
  8500, 10000, 12000, 15000, 18000, 22000, 24000, 25000, 30000, 50000,
];

function formatFromPrice(amount: number, formatMoney: (n: number) => string): string {
  return `from ${formatMoney(amount)}`;
}

function applyMarketingAmountTokens(text: string, formatMoney: (n: number) => string): string {
  let out = text;
  const replaceFor = (amount: number, suffix: string) => {
    const fromPrice = formatFromPrice(amount, formatMoney);
    const FromPrice = fromPrice.charAt(0).toUpperCase() + fromPrice.slice(1);
    const amountStr = formatMoney(amount);
    out = out.replace(new RegExp(`\\{FromPrice${suffix}\\}`, 'g'), FromPrice);
    out = out.replace(new RegExp(`\\{fromPrice${suffix}\\}`, 'g'), fromPrice);
    out = out.replace(new RegExp(`\\{amount${suffix}\\}`, 'g'), amountStr);
  };

  for (const amount of LLMS_TIER_AMOUNTS) {
    replaceFor(amount, String(amount));
  }
  for (const amount of LLMS_COMMON_AMOUNTS) {
    replaceFor(amount, String(amount));
  }

  return out;
}

function localizeMarketingCurrencyInText(text: string, formatMoney: (n: number) => string): string {
  if (!text || !text.includes('₹')) return text;

  let out = text.replace(/₹\s*([\d.]+)\s*L/gi, (_, lakh) => {
    const amount = parseFloat(lakh) * 100000;
    if (!Number.isFinite(amount)) return `₹${lakh}L`;
    return formatMoney(amount);
  });

  out = out.replace(/₹\s*([\d,]+(?:\.\d+)?)\s*K/gi, (_, raw) => {
    const base = Number(String(raw).replace(/,/g, ''));
    if (!Number.isFinite(base)) return `₹${raw}K`;
    return formatMoney(base * 1000);
  });

  out = out.replace(/₹\s*([\d,]+(?:\.\d+)?)/g, (_, raw) => {
    const amount = Number(String(raw).replace(/,/g, ''));
    if (!Number.isFinite(amount)) return `₹${raw}`;
    return formatMoney(amount);
  });

  return out;
}

/** Patch llms.txt amount tokens and legacy INR literals for tenant currency. */
export function patchLlmsMarketingText(text: string, formatMoney: (amount: number) => string): string {
  if (
    !text.includes('{fromPrice') &&
    !text.includes('{FromPrice') &&
    !text.includes('{amount') &&
    !text.includes('₹')
  ) {
    return text;
  }
  const withTokens = applyMarketingAmountTokens(text, formatMoney);
  return localizeMarketingCurrencyInText(withTokens, formatMoney);
}

/** Known llms.txt sections configurable per org/platform (matches public/llms.txt). */
export const LLMS_MARKETING_SECTIONS = [
  { id: 'company-overview', label: 'Company Overview' },
  { id: 'key-facts', label: 'Key Facts' },
  { id: 'services-offered', label: 'Services Offered' },
  { id: 'service-areas', label: 'Service Areas' },
  { id: 'differentiators', label: 'Differentiators' },
  { id: 'company-timeline', label: 'Company Timeline' },
  { id: 'customer-reviews-summary', label: 'Customer Reviews Summary' },
  { id: 'frequently-asked-questions', label: 'Frequently Asked Questions' },
  { id: 'contact-information', label: 'Contact Information' },
] as const;

export function slugifyLlmsSectionHeading(title: string): string {
  return title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/** Remove disabled `##` sections from the llms.txt marketing template. */
export function filterLlmsTemplateSections(
  template: string,
  disabledSectionIds: readonly string[] = [],
): string {
  if (!template || !disabledSectionIds.length) return template;
  const disabled = new Set(disabledSectionIds.map((id) => id.trim()).filter(Boolean));
  if (!disabled.size) return template;

  const parts = template.split(/^## /m);
  if (parts.length <= 1) return template;

  const preamble = parts[0]?.trimEnd() ?? '';
  const keptSections = parts.slice(1).filter((block) => {
    const heading = block.split('\n')[0]?.trim() ?? '';
    const id = slugifyLlmsSectionHeading(heading);
    return !disabled.has(id);
  });

  const body = keptSections.map((block) => `## ${block}`).join('\n\n');
  return [preamble, body].filter(Boolean).join('\n\n');
}

export type LlmsTemplateSection = {
  id: string;
  label: string;
  charCount: number;
};

export type LlmsSectionVisibility = LlmsTemplateSection & {
  enabled: boolean;
};

/** Parsed `##` sections from the llms.txt marketing template. */
export function listLlmsTemplateSections(template: string): LlmsTemplateSection[] {
  if (!template) return [];
  const parts = template.split(/^## /m);
  if (parts.length <= 1) return [];

  return parts.slice(1).map((block) => {
    const label = block.split('\n')[0]?.trim() ?? '';
    return {
      id: slugifyLlmsSectionHeading(label),
      label,
      charCount: block.trim().length,
    };
  });
}

const LLMS_SECTION_LABEL_BY_ID = new Map<string, string>(
  LLMS_MARKETING_SECTIONS.map((section) => [section.id, section.label]),
);

/** Enabled/disabled llms.txt sections for admin preview. */
export function summarizeLlmsSectionVisibility(
  template: string,
  disabledSectionIds: readonly string[] = [],
): LlmsSectionVisibility[] {
  const disabled = new Set(disabledSectionIds.map((id) => id.trim()).filter(Boolean));
  return listLlmsTemplateSections(template).map((section) => ({
    ...section,
    label: LLMS_SECTION_LABEL_BY_ID.get(section.id) ?? section.label,
    enabled: !disabled.has(section.id),
  }));
}

/** Section ids toggled between two disabled-section lists. */
export function diffLlmsDisabledSections(
  previous: readonly string[],
  next: readonly string[],
): { newlyDisabled: string[]; newlyEnabled: string[] } {
  const prev = new Set(previous.map((id) => id.trim()).filter(Boolean));
  const nxt = new Set(next.map((id) => id.trim()).filter(Boolean));
  return {
    newlyDisabled: [...nxt].filter((id) => !prev.has(id)),
    newlyEnabled: [...prev].filter((id) => !nxt.has(id)),
  };
}

export type TenantLlmsTxtOptions = MarketingBrandingSubstituteOptions & {
  formatMoney?: (amount: number) => string;
  currencyCode?: string;
  disabledSections?: readonly string[];
};

/** Build tenant-branded llms.txt from the marketing corpus template. */
export function buildTenantLlmsTxt(template: string, options?: TenantLlmsTxtOptions): string {
  if (!template) return '';
  const filtered = filterLlmsTemplateSections(template, options?.disabledSections);
  const currencyCode = options?.currencyCode?.trim() || 'INR';
  const formatMoney =
    options?.formatMoney ?? ((amount: number) => formatTenantCurrency(Math.round(amount), currencyCode));
  const patched = patchLlmsMarketingText(filtered, formatMoney);
  return substituteMarketingBrandingInText(patched, options);
}

/** Preview tenant llms.txt in admin SEO panel. */
export function buildTenantLlmsTxtPreview(
  template: string,
  branding: MarketingBrandingSubstituteOptions,
  options?: { currencyCode?: string; disabledSections?: readonly string[] },
): string {
  return buildTenantLlmsTxt(template, { ...branding, ...options });
}
